'use client'

import { type FormEventHandler, useEffect, useRef, useState } from 'react'
import type { MobileIdAuthenticationHash } from '@/root/shared-types/api/auth'

type MobileIdTransaction = {
  authenticationHash: MobileIdAuthenticationHash
  personalIdentificationNumber: string
  phoneNumber: string
  verificationCode: string
}

type UseMobileIdFlowOptions = {
  onSuccess: (accessToken: string) => void | Promise<void>
  locale?: string | null
  messages: {
    invalidRequest: string
    prepareError: string
    processError: string
  }
}

const MOBILE_ID_PREPARE_PATH = '/api/auth/mobile-id/prepare'
const MOBILE_ID_PROCESS_PATH = '/api/auth/mobile-id/process'

const buildRequestHeaders = (locale: string | null | undefined): HeadersInit => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (locale) {
    headers['x-locale'] = locale
  }
  return headers
}

export const useMobileIdFlow = ({ onSuccess, locale, messages }: UseMobileIdFlowOptions) => {
  const [transaction, setTransaction] = useState<MobileIdTransaction | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const onSuccessRef = useRef(onSuccess)
  const messagesRef = useRef(messages)
  const localeRef = useRef(locale)
  const processAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  useEffect(() => {
    return () => {
      processAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!transaction) return

    let cancelled = false
    processAbortRef.current?.abort()
    const controller = new AbortController()
    processAbortRef.current = controller

    const runProcess = async () => {
      setIsProcessing(true)

      try {
        const response = await fetch(MOBILE_ID_PROCESS_PATH, {
          method: 'POST',
          headers: buildRequestHeaders(localeRef.current),
          body: JSON.stringify({
            personalIdentificationNumber: transaction.personalIdentificationNumber,
            phoneNumber: transaction.phoneNumber,
            authenticationHash: transaction.authenticationHash,
          }),
          cache: 'no-store',
          signal: controller.signal,
        })

        const data = (await response.json().catch(() => null)) as { accessToken?: string; message?: string } | null

        if (cancelled || controller.signal.aborted) return

        if (!response.ok) {
          setTransaction(null)
          setErrorMessage(data?.message ?? messagesRef.current.processError)
          return
        }

        if (!data?.accessToken) {
          setTransaction(null)
          setErrorMessage(messagesRef.current.invalidRequest)
          return
        }

        await onSuccessRef.current(data.accessToken)
      } catch (error) {
        if (cancelled || controller.signal.aborted) return

        setTransaction(null)
        setErrorMessage(messagesRef.current.processError)
      } finally {
        if (!cancelled) {
          setIsProcessing(false)
        }
      }
    }

    void runProcess()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [transaction])

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    setIsPreparing(true)
    setIsProcessing(false)
    setErrorMessage(null)
    setTransaction(null)
    processAbortRef.current?.abort()

    const formData = new FormData(event.currentTarget)
    const payload = {
      personalIdentificationNumber: String(formData.get('personalIdentificationNumber') ?? '').trim(),
      phoneNumber: String(formData.get('phoneNumber') ?? '').trim(),
    }

    try {
      const response = await fetch(MOBILE_ID_PREPARE_PATH, {
        method: 'POST',
        headers: buildRequestHeaders(localeRef.current),
        body: JSON.stringify(payload),
        cache: 'no-store',
      })

      const data = (await response.json().catch(() => null)) as
        | {
            verificationCode?: string
            authenticationHash?: MobileIdAuthenticationHash
            personalIdentificationNumber?: string
            phoneNumber?: string
            message?: string
          }
        | null

      if (!response.ok) {
        setErrorMessage(data?.message ?? messagesRef.current.prepareError)
        return
      }

      if (
        !data?.verificationCode ||
        !data.authenticationHash ||
        !data.personalIdentificationNumber ||
        !data.phoneNumber
      ) {
        setErrorMessage(data?.message ?? messagesRef.current.invalidRequest)
        return
      }

      setTransaction({
        verificationCode: data.verificationCode,
        authenticationHash: data.authenticationHash,
        personalIdentificationNumber: data.personalIdentificationNumber,
        phoneNumber: data.phoneNumber,
      })
    } catch {
      setErrorMessage(messagesRef.current.prepareError)
    } finally {
      setIsPreparing(false)
    }
  }

  return {
    errorMessage,
    handleSubmit,
    isLoading: isPreparing || isProcessing || Boolean(transaction),
    isPreparing,
    isProcessing,
    isWaitingForConfirmation: Boolean(transaction),
    verificationCode: transaction?.verificationCode ?? null,
  }
}
